import { parseNrrdWasm } from '../visualization/volumeWasm';
/**
 * Reads a NRRD volume.
 *
 * The reading itself is Rust (`wasm/pointcloud-parser/src/volume/nrrd.rs`):
 * header, affine, endianness, the raw/gzip/ascii encodings and the channel
 * split. What is left here is resolving a detached data file, which needs the
 * host's file access, and shaping the result into `VolumeData`.
 */
export class NrrdParser {
    async parse(data, fileName, timingCallback, resolveDataFile) {
        const start = performance.now();
        timingCallback?.('🔍 NRRD: reading header...');
        // A detached header names its payload; the bytes have to be fetched here,
        // where the host's file access lives, and handed to the reader.
        let detached = new Uint8Array(0);
        const relative = detachedDataFile(data);
        if (relative) {
            if (relative.startsWith('LIST')) {
                throw new Error('Detached NRRD "data file: LIST" (one file per slice) is not supported');
            }
            if (!resolveDataFile) {
                throw new Error(`NRRD header references a detached data file "${relative}" but no way to read it was provided`);
            }
            const resolved = await resolveDataFile(relative);
            detached = new Uint8Array(resolved);
        }
        const volume = await parseNrrdWasm(data, detached);
        timingCallback?.(`✅ NRRD: ${volume.sizes.join('x')} volume in ${(performance.now() - start).toFixed(1)}ms`);
        const header = volume.header;
        return {
            sizes: volume.sizes,
            samples: volume.samples,
            ijkToWorld: volume.ijkToWorld,
            spaceUnits: readSpaceUnits(header),
            intensityUnits: readIntensityUnits(header),
            range: readRange(header),
            channels: volume.channels,
            header,
            fileName,
        };
    }
}
/** The `data file` field, read from the header text without decoding the body. */
function detachedDataFile(data) {
    const limit = Math.min(data.length, 1 << 16);
    let text = '';
    for (let i = 0; i < limit; i++) {
        text += String.fromCharCode(data[i]);
    }
    const match = text.match(/^\s*data\s*file\s*:\s*(.+)$/im);
    return match?.[1]?.trim();
}
function readSpaceUnits(header) {
    const declared = header['space units'];
    if (declared) {
        const first = declared.match(/"([^"]*)"/);
        if (first?.[1]) {
            return first[1];
        }
    }
    // Medical NRRD without explicit units is millimetres by convention.
    return 'mm';
}
/**
 * Intensity semantics, which NRRD has no standard field for.
 *
 * `units:=HU` is what the tiff-visualizer bridge writes after applying
 * RescaleSlope/RescaleIntercept. `modality` is the DICOM-derived fallback so a
 * CT written by another tool still gets Hounsfield-aware defaults.
 */
function readIntensityUnits(header) {
    const explicit = (header['units'] || header['intensity units'] || '').replace(/"/g, '').trim();
    if (explicit) {
        return explicit;
    }
    const modality = (header['modality'] || '').trim().toUpperCase();
    if (modality === 'CT' || modality.endsWith(':CT')) {
        return 'HU';
    }
    return undefined;
}
function readRange(header) {
    const min = Number(header['min']);
    const max = Number(header['max']);
    if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
        return { min, max };
    }
    return undefined;
}
